/**
 * T038: Client Dashboard Component
 * 
 * Main dashboard for client project management and onboarding
 * Constitutional compliance: <2s page loads, WCAG 2.1 AA accessibility, TDD coverage
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  LinearProgress,
  Alert,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  WorkOutline as ProjectIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';

import { usePermissions, PermissionGuard } from '../../contexts/PermissionsContext';
import useProjectService, { Project, ProjectFilters } from '../../services/ProjectService';
import useOnboardingService, { OnboardingSession } from '../../services/onboardingService';
import OnboardingWizardV2 from '../onboarding/OnboardingWizardV2';
import { AIConsole } from '../ai-console';

interface ClientDashboardProps {
  onError: (error: string) => void;
}

const ClientDashboard: React.FC<ClientDashboardProps> = ({ onError }) => {
  const { hasPermission } = usePermissions();
  const [projects, setProjects] = useState<Project[]>([]);
  const [onboardingSessions, setOnboardingSessions] = useState<OnboardingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const {
    getProjects,
    updateProjectStatus
  } = useProjectService();

  const {
    getOnboardingHistory
  } = useOnboardingService();

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [projectsResult, historyResult] = await Promise.all([
          getProjects({ per_page: 10 }), // Remove status filter to show all projects including drafts
          getOnboardingHistory(1, 5)
        ]);

        setProjects(projectsResult || []);
        setOnboardingSessions(historyResult?.data || []);
      } catch (err) {
        const errorMessage = err instanceof Error
          ? err.message 
          : 'Failed to load dashboard data';
        setError(errorMessage);
        onError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [getProjects, getOnboardingHistory, onError]);

  // Handle project status updates
  const handleStatusUpdate = async (projectId: number, newStatus: string) => {
    try {
      await updateProjectStatus(projectId, newStatus);
      
      // Refresh projects list
      const projectsResult = await getProjects({ status: 'active', per_page: 10 });
      setProjects(projectsResult || []);
      
      setAnchorEl(null);
      setSelectedProject(null);
    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message 
        : 'Failed to update project status';
      setError(errorMessage);
      onError(errorMessage);
    }
  };

  // Handle onboarding completion
  const handleOnboardingComplete = (projectId: number) => {
    setShowOnboarding(false);
    // Refresh dashboard data
    window.location.reload(); // Simple refresh for now
  };

  // Handle menu actions
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, project: Project) => {
    setAnchorEl(event.currentTarget);
    setSelectedProject(project);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  // Get status color
  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'active': return 'primary';
      case 'completed': return 'success';
      case 'on_hold': return 'warning';
      case 'cancelled': return 'error';
      case 'planning': return 'info';
      default: return 'default';
    }
  };

  // Get onboarding status icon
  const getOnboardingStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon color="success" />;
      case 'in_progress': return <ScheduleIcon color="primary" />;
      case 'abandoned': return <WarningIcon color="warning" />;
      default: return <InfoIcon color="info" />;
    }
  };

  if (showOnboarding) {
    return (
      <OnboardingWizardV2
        onComplete={handleOnboardingComplete}
        onError={onError}
      />
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" component="h1">
            Client Dashboard
          </Typography>
          {hasPermission('project.create') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowOnboarding(true)}
            >
              Start New Project
            </Button>
          )}
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ width: '100%', mb: 3 }}>
          <LinearProgress />
        </Box>
      ) : (
        <Stack spacing={4}>
          {/* Projects Section */}
          <PermissionGuard requiredPermissions={['project.view']}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Your Projects
                </Typography>
                {!projects || projects.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      No projects found. Start your first project!
                    </Typography>
                    {hasPermission('project.create') && (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setShowOnboarding(true)}
                        sx={{ mt: 2 }}
                      >
                        Start New Project
                      </Button>
                    )}
                  </Box>
                ) : (
                  <Stack spacing={2}>
                    {projects.map((project) => (
                      <Paper key={project.id} sx={{ p: 2 }} elevation={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6">{project.name}</Typography>
                            <Typography color="text.secondary" sx={{ mb: 1 }}>
                              {project.description}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip 
                                label={project.status} 
                                color={getStatusColor(project.status)}
                                size="small"
                              />
                              <Typography variant="caption" color="text.secondary">
                                Created: {new Date(project.created_at).toLocaleDateString()}
                              </Typography>
                            </Stack>
                          </Box>
                          {hasPermission('project.edit') && (
                            <IconButton
                              onClick={(e) => handleMenuClick(e, project)}
                              size="small"
                            >
                              <MoreVertIcon />
                            </IconButton>
                          )}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </PermissionGuard>

          {/* Onboarding History */}
          <PermissionGuard requiredPermissions={['communication.view']}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Recent Onboarding
                </Typography>
                {!onboardingSessions || onboardingSessions.length === 0 ? (
                  <Typography color="text.secondary">
                    No onboarding sessions found.
                  </Typography>
                ) : (
                  <List dense>
                    {onboardingSessions.map((session) => (
                      <React.Fragment key={session.id}>
                        <ListItem>
                          <ListItemIcon>
                            {getOnboardingStatusIcon(session.status)}
                          </ListItemIcon>
                          <ListItemText
                            primary={`Session ${session.id}`}
                            secondary={
                              <Stack spacing={0.5}>
                                <Typography variant="caption">
                                  Status: {session.status}
                                </Typography>
                                <Typography variant="caption">
                                  Progress: {session.completion_percentage}%
                                </Typography>
                                <Typography variant="caption">
                                  Updated: {new Date(session.updated_at).toLocaleDateString()}
                                </Typography>
                              </Stack>
                            }
                          />
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </PermissionGuard>

          {/* AI Console */}
          <Card>
            <CardContent>
              <Box sx={{ height: '70vh' }}>
                <AIConsole defaultContextType="general" />
              </Box>
            </CardContent>
          </Card>
        </Stack>
      )}

      {/* Project Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedProject && handleStatusUpdate(selectedProject.id, 'active')}>
          Mark as Active
        </MenuItem>
        <MenuItem onClick={() => selectedProject && handleStatusUpdate(selectedProject.id, 'on_hold')}>
          Put on Hold
        </MenuItem>
        <MenuItem onClick={() => selectedProject && handleStatusUpdate(selectedProject.id, 'completed')}>
          Mark as Completed
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => selectedProject && handleStatusUpdate(selectedProject.id, 'cancelled')}>
          Cancel Project
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default ClientDashboard;